import { useGetDriveViewV2 } from '@commenda/api';
import { saveAgentGeneratedFile } from '@commenda/api/commenda-ai/agentPlatformApi';
import type {
  CommendaAiGeneratedFileRef,
  CommendaAiGeneratedFileSuggestion,
  CommendaAiSavedFile,
} from '@commenda/api/commenda-ai/types';
import { useGeneratedFileSaveOptions } from '@commenda/api/commenda-ai/useGeneratedFileSaveOptions';
import {
  Alert,
  Button,
  DateField,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Loading,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  showNotification,
} from '@commenda/kakashi';
import { useMemo, useState } from 'react';
import { FolderPicker } from '../document-views-v2/FolderPicker';
import { buildBreadcrumbPath } from '../document-views-v2/utils';
import { CorporationSelector } from '../molecules/CorporationSelector/CorporationSelector';

const NO_DOCUMENT_TYPE = 'NO_DOCUMENT_TYPE';

interface SaveToDocumentsModalProps {
  isOpen: boolean;
  companyId: number;
  fileRef: CommendaAiGeneratedFileRef;
  fileName: string;
  suggestion: CommendaAiGeneratedFileSuggestion;
  onClose: () => void;
  onSaved: (saved: CommendaAiSavedFile) => void;
}

/** Every field starts from what the agent suggested and is shown for the user to confirm or change;
 * nothing is written until Save, and only what the dialog shows is sent. Mount once per open. */
export function SaveToDocumentsModal({
  isOpen,
  companyId,
  fileRef,
  fileName,
  suggestion,
  onClose,
  onSaved,
}: SaveToDocumentsModalProps) {
  const [entityId, setEntityId] = useState(suggestion.entityId);
  const [requestedType, setRequestedType] = useState(
    suggestion.documentSubType
  );
  const [effectiveStartDate, setEffectiveStartDate] = useState<
    string | undefined
  >(suggestion.effectiveStartDate);
  const [effectiveEndDate, setEffectiveEndDate] = useState<string | undefined>(
    suggestion.effectiveEndDate
  );
  const [chosenFolderId, setChosenFolderId] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();

  const {
    saveOptions,
    isLoading: isSaveOptionsLoading,
    isRefreshing: isSaveOptionsRefreshing,
    error: saveOptionsError,
    refresh: refreshSaveOptions,
  } = useGeneratedFileSaveOptions(fileRef, entityId, requestedType);
  const { data: driveView, error: driveViewError } = useGetDriveViewV2({
    type: 'corporation',
    corporationId: entityId ?? 0,
  });

  const rootFolder = driveView?.folder ?? null;
  const documentType = saveOptions?.documentType ?? null;
  const isPeriodRequired = documentType?.isTimeSensitive ?? false;
  const defaultFolderId = saveOptions?.folder?.id ?? null;
  const folderId = chosenFolderId ?? defaultFolderId;
  const isRequestedTypeUnavailable =
    requestedType !== undefined && saveOptions !== undefined && !documentType;

  const periodError = useMemo(() => {
    if (!isPeriodRequired) return null;
    if (!effectiveStartDate || !effectiveEndDate)
      return `${documentType?.documentName} covers a period. Choose its start and end dates.`;
    if (effectiveStartDate > effectiveEndDate)
      return 'The end date must not be before the start date.';
    return null;
  }, [isPeriodRequired, effectiveStartDate, effectiveEndDate, documentType]);

  const folderPath = useMemo(() => {
    if (!rootFolder || !folderId) return null;
    const names = buildBreadcrumbPath(rootFolder, folderId)
      .slice(1)
      .map((folder) => folder.name);
    if (names.length > 0) return names.join(' / ');
    return folderId === defaultFolderId
      ? (saveOptions?.folder?.path ?? null)
      : null;
  }, [rootFolder, folderId, defaultFolderId, saveOptions]);

  const initialBrowseFolderId = useMemo(() => {
    if (!rootFolder || !defaultFolderId) return null;
    return buildBreadcrumbPath(rootFolder, defaultFolderId).at(-2)?.id ?? null;
  }, [rootFolder, defaultFolderId]);

  const isLoadingFolders =
    entityId !== undefined &&
    (isSaveOptionsLoading || (!rootFolder && !driveViewError));
  const loadError = saveOptionsError ?? driveViewError;

  const handleSelectEntity = (nextEntityId: number) => {
    setEntityId(nextEntityId);
    setChosenFolderId(undefined);
    setSaveError(undefined);
  };

  const handleSelectType = (value: string) => {
    setRequestedType(value === NO_DOCUMENT_TYPE ? undefined : value);
    setChosenFolderId(undefined);
    setSaveError(undefined);
  };

  const handleSave = async () => {
    if (entityId === undefined || !folderId || periodError) return;

    setIsSaving(true);
    setSaveError(undefined);
    const result = await saveAgentGeneratedFile({
      ...fileRef,
      entityId,
      folderId,
      documentSubType: documentType?.documentSubType,
      effectiveStartDate: isPeriodRequired ? effectiveStartDate : undefined,
      effectiveEndDate: isPeriodRequired ? effectiveEndDate : undefined,
      replaceExisting: Boolean(saveOptions?.replacesFileName),
    });
    setIsSaving(false);

    if (!result.success || !result.data) {
      setSaveError(result.error ?? 'Could not save the file to Documents.');
      if (result.status === 409) await refreshSaveOptions();
      return;
    }

    showNotification({
      title: 'Saved to Documents',
      message: folderPath ?? undefined,
      valid: true,
    });
    onSaved(result.data);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] flex flex-col w-[560px] max-w-[95vw]">
        <DialogHeader>
          <DialogTitle className="min-w-0 break-words">
            Save &quot;{fileName}&quot; to Documents
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-2xl p-3xl flex-1 min-h-0 overflow-y-auto">
          <CorporationSelector
            companyId={companyId}
            label="Entity"
            value={entityId ?? null}
            onChange={handleSelectEntity}
            showAddEntity={false}
          />

          {entityId !== undefined && saveOptions && (
            <div className="flex flex-col gap-sm">
              <Label>Document type</Label>
              <Select
                value={documentType?.documentSubType ?? NO_DOCUMENT_TYPE}
                onValueChange={handleSelectType}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_DOCUMENT_TYPE}>
                    No document type
                  </SelectItem>
                  {saveOptions.documentTypes.map((option) => (
                    <SelectItem
                      key={option.documentSubType}
                      value={option.documentSubType}
                    >
                      {option.documentName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isRequestedTypeUnavailable && (
                <span className="txt-extra-small-regular text-text-secondary">
                  The suggested type doesn&apos;t apply to this entity. Pick
                  one, or save it without a type.
                </span>
              )}
            </div>
          )}

          {isPeriodRequired && (
            <div className="flex flex-col gap-sm">
              <Label>Period covered</Label>
              <div className="flex items-start gap-lg">
                <DateField
                  aria-label="Period start"
                  value={effectiveStartDate}
                  onChange={setEffectiveStartDate}
                />
                <span className="pt-sm txt-small-regular text-text-secondary">
                  to
                </span>
                <DateField
                  aria-label="Period end"
                  value={effectiveEndDate}
                  onChange={setEffectiveEndDate}
                />
              </div>
              {periodError && (
                <span className="txt-extra-small-regular text-text-error-primary">
                  {periodError}
                </span>
              )}
            </div>
          )}

          {entityId !== undefined && (
            <div className="flex flex-col gap-sm min-h-[240px]">
              <Label>Folder</Label>
              {loadError ? (
                <Alert
                  variant="destructive"
                  description="Couldn't load this entity's folders. Try again in a moment."
                />
              ) : isLoadingFolders || !rootFolder ? (
                <Loading />
              ) : (
                <>
                  <FolderPicker
                    key={`${entityId}-${defaultFolderId ?? 'root'}`}
                    rootFolder={rootFolder}
                    rootLabel="Documents"
                    selectedFolderId={folderId}
                    onSelectFolder={setChosenFolderId}
                    initialBrowseFolderId={initialBrowseFolderId}
                  />
                  {folderPath && (
                    <span className="txt-extra-small-regular text-text-secondary">
                      Saving to {folderPath}
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {saveOptions?.replacesFileName && documentType && (
            <Alert
              variant="warning"
              title={`This entity already has ${documentType.documentName}`}
              description={`Saving will replace "${saveOptions.replacesFileName}" as the current one. The existing file stays in Documents.`}
            />
          )}

          {saveError && <Alert variant="destructive" description={saveError} />}
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={
              isSaving ||
              isSaveOptionsRefreshing ||
              entityId === undefined ||
              !folderId ||
              periodError !== null
            }
          >
            {saveOptions?.replacesFileName ? 'Replace and save' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
